import React, { useState } from 'react';
import { 
  HelpCircle, MessageCircle, BookOpen, Mail, ChevronDown, ChevronRight,
  Zap, Users, Target, Globe, CreditCard, Shield, ExternalLink
} from 'lucide-react';

const COLORS = {
  primary: '#2563EB',
  accent: 'var(--text-primary)',
  light: 'var(--bg-subtle)',
  background: 'var(--bg-canvas)',
};

const FAQ_SECTIONS = [
  {
    title: 'Getting Started',
    icon: Zap,
    items: [
      { q: 'How do I set up my company profile?', a: 'Go to Settings > Company Info to fill in your company details, location, and contact information. Then visit Branding & CSR to set your logo, mission statement, and focus areas. Completing your profile unlocks AI-powered charity matching.' },
      { q: 'How do I create my first campaign?', a: 'Click Campaigns in the top navigation, then click "+ New Campaign". You can browse charities, use AI recommendations, or build a portfolio. Select a charity, set your goal amount, and optionally enable employer matching.' },
      { q: 'How do employees donate?', a: 'Share your Impact Portal URL (found in Settings > Impact Portal) with employees. They can browse active campaigns and donate directly. Donations are processed securely through Every.org.' },
    ]
  },
  {
    title: 'Campaigns & Donations',
    icon: Target,
    items: [
      { q: 'Do you take a cut from donations?', a: 'Never — Altrunic takes 0% of donations and charges only the platform subscription. Donations are processed and receipted by Every.org, which also charges no platform fee; only card processing (2.2% + $0.30) applies, and donors can choose to cover it. Bank transfers, stock and DAF gifts reach the charity at 100%.' },
      { q: 'How does employer matching work?', a: 'When creating a campaign, enable "Employer Match" and set your match ratio (e.g., 1:1). When employees donate, the matched amount is tracked automatically. You can view and record matching payments in Analytics > Reports > Matching Report.' },
      { q: 'Can guests (non-employees) donate?', a: 'Yes, if enabled in Settings > Impact Portal. Guest donations are tracked separately from employee donations for reporting purposes.' },
    ]
  },
  {
    title: 'Billing & Plans',
    icon: CreditCard,
    items: [
      { q: 'What plans are available?', a: 'We offer three tiers: Starter (Free) with 1 campaign and 25 employees, Professional ($99/mo) with unlimited campaigns and 250 employees, and Enterprise ($299/mo) with unlimited everything plus white-label domain support.' },
      { q: 'Can I cancel anytime?', a: 'Yes, cancel anytime from your Billing settings. You keep full access until the end of your current billing period. No questions asked.' },
      { q: 'How do upgrades and downgrades work?', a: 'Upgrades take effect immediately. Downgrades are scheduled for the end of your current billing period to ensure uninterrupted service. Your data is always preserved.' },
    ]
  },
  {
    title: 'Team & Management',
    icon: Users,
    items: [
      { q: 'How do I invite team members?', a: 'Go to Team > Members and click "Invite". Enter their email and select a role (admin, manager, or employee). They will receive login credentials.' },
      { q: 'What are the different user roles?', a: 'Admin: Full platform access including billing and settings. Manager: Can manage campaigns, view reports, and manage team. Employee: Can donate, volunteer, and view their own activity.' },
    ]
  },
  {
    title: 'Impact Portal & White-Label',
    icon: Globe,
    items: [
      { q: 'What is the Impact Portal?', a: 'Your public-facing donation page where employees and guests can donate to your active campaigns. It is automatically generated and branded with your company info.' },
      { q: 'How do I set up a custom domain?', a: 'Enterprise plan users can configure a custom domain (e.g., impact.yourcompany.com) in Settings > White Label. Add a CNAME record pointing to cname.altrunic.com, then verify it in the dashboard.' },
    ]
  },
  {
    title: 'Security & Privacy',
    icon: Shield,
    items: [
      { q: 'Is my data secure?', a: 'Yes. All data is encrypted in transit (HTTPS) and at rest. We follow industry-standard security practices and never share your data with third parties.' },
      { q: 'Are donations tax-deductible?', a: 'Yes. Donations made through Every.org are tax-deductible. Tax receipts are available in Analytics > Reports > Tax Receipts.' },
    ]
  },
];

const HelpSupportView = () => {
  const [expandedSection, setExpandedSection] = useState(0);
  const [expandedItems, setExpandedItems] = useState({});

  const toggleItem = (sectionIdx, itemIdx) => {
    const key = `${sectionIdx}-${itemIdx}`;
    setExpandedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" style={{ background: COLORS.background }}>
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold" style={{ color: COLORS.primary }}>Help & Support</h2>
        <p className="text-sm text-gray-500 mt-1">Find answers, learn best practices, and get in touch</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-3 gap-4 mb-8" data-testid="help-quick-actions">
        <button
          data-testid="help-chat-btn"
          onClick={() => {
            const chatBtn = document.querySelector('[data-testid="chatbot-toggle"]') || document.querySelector('.fixed.bottom-6.right-6 button');
            if (chatBtn) chatBtn.click();
          }}
          className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all text-left group"
        >
          <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: COLORS.primary + '12' }}>
            <MessageCircle className="w-5 h-5" style={{ color: COLORS.primary }} />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Chat with Support</h3>
          <p className="text-xs text-gray-500">Get instant help from our AI assistant</p>
        </button>

        <a
          href="mailto:support@altrunic.com"
          data-testid="help-email-btn"
          className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all text-left group"
        >
          <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3 bg-green-50">
            <Mail className="w-5 h-5 text-green-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Email Support</h3>
          <p className="text-xs text-gray-500">support@altrunic.com</p>
        </a>

        <a
          href="https://every.org"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="help-docs-btn"
          className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all text-left group"
        >
          <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3 bg-purple-50">
            <BookOpen className="w-5 h-5 text-purple-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-1">
            Charity Directory <ExternalLink className="w-3 h-3 text-gray-400" />
          </h3>
          <p className="text-xs text-gray-500">Browse 1.8M+ verified charities on Every.org</p>
        </a>
      </div>

      {/* FAQ Sections */}
      <div data-testid="help-faq">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Frequently Asked Questions</h3>
        <div className="space-y-3">
          {FAQ_SECTIONS.map((section, si) => (
            <div key={si} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <button
                data-testid={`faq-section-${si}`}
                onClick={() => setExpandedSection(expandedSection === si ? -1 : si)}
                className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-all"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: COLORS.primary + '10' }}>
                  <section.icon className="w-4 h-4" style={{ color: COLORS.primary }} />
                </div>
                <span className="font-semibold text-gray-900 flex-1">{section.title}</span>
                <span className="text-xs text-gray-400 mr-2">{section.items.length} questions</span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedSection === si ? 'rotate-180' : ''}`} />
              </button>

              {expandedSection === si && (
                <div className="border-t border-gray-100">
                  {section.items.map((item, ii) => {
                    const key = `${si}-${ii}`;
                    const isOpen = expandedItems[key];
                    return (
                      <div key={ii} className="border-b border-gray-50 last:border-0">
                        <button
                          data-testid={`faq-item-${si}-${ii}`}
                          onClick={() => toggleItem(si, ii)}
                          className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-gray-50/50 transition-all"
                        >
                          <ChevronRight className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                          <span className="text-sm font-medium text-gray-700">{item.q}</span>
                        </button>
                        {isOpen && (
                          <div className="px-5 pb-4 pl-12">
                            <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 p-5 bg-white rounded-xl border border-gray-200 flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-gray-900">Still need help?</h4>
          <p className="text-sm text-gray-500">Our team typically responds within 24 hours</p>
        </div>
        <a
          href="mailto:support@altrunic.com"
          className="px-5 py-2.5 text-white text-sm font-semibold rounded-lg transition-all hover:opacity-90"
          style={{ background: COLORS.primary }}
        >
          Contact Support
        </a>
      </div>
    </div>
  );
};

export default HelpSupportView;
